import React, { useEffect, useState } from 'react';
import {
  Button,
  Heading,
  Link,
  Rank,
  Responsive,
  Sizes,
  Typography,
  VerticalSpacing,
  VerticalSpacingSizes,
  getClassNameFactory,
  getRehydratableName,
} from '@dcl/ui';
import { propsToDataAttributes } from '@tr-digital/react-rehydrate';
import { IFileDownloadExperience } from './types';
import { boldTypes, types } from '@dcl/ui/dist/elements/Heading';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import { DESKTOP_MIN_WIDTH, MOBILE_MAX_WIDTH } from './constants';
import DownloadIcon from './icons/DownloadIcon';
import { convertTableValue, convertToTableHeaderColumns } from './helper';
import { FileDownloadExperienceSearch } from './components/FileDownloadExperienceSearch/FileDownloadExperienceSearch';
import { FileDownloadExperienceSearchResultDekstop } from './components/FileDownloadExperienceSearchResultDekstop/FileDownloadExperienceSearchResultDekstop';
import { FileDownloadExperienceSearchResultMobile } from './components/FileDownloadExperienceSearchResultMobile/FileDownloadExperienceSearchResultMobile';
import {
  ButtonColor,
  ButtonKind,
  IconPosition,
} from '@dcl/ui/dist/elements/Button';
import { LinkTypes } from '@dcl/ui/dist/elements/Link';
import './index.css';
import { EmptySearch } from './components/EmptySearch/EmptySearch';
import LinkIcon from './icons/LinkIcon';
import { getValueFromCookie } from '../../utils/helper';
import { CIAM_TOKEN_VAL_URL, PORTAL_BASE_URL } from '../../api';
import AlertIcon from './icons/AlertIcon';
import { HS_ACCESS_TOKEN, StatusCodes } from '../CiamLogin/constants';
import { validateToken } from '../CiamLogin/utils';
import { External_Bearer_Token } from './mock';

// Extracted helper function for downloading files
const downloadFile = async (downloadApiHost: string, filePath: string) => {
  if (String(filePath).toLowerCase().includes('content/dam')) {
    const filename = filePath.replace(/^.*[\\/]/, '');
    const link = document.createElement('a');
    link.href = filePath;
    link.download = filename;
    link.click();
  } else {
    const isExternal = String(filePath).toLowerCase().includes('/external/');

    if (filePath.startsWith('/')) {
      filePath = filePath.substring(1);
    }

    const path = `/files/download/${encodeURIComponent(filePath)}`;

    const url = `${downloadApiHost}${path}`;
    const token = getValueFromCookie(HS_ACCESS_TOKEN);

    let bearerToken = '';
    if (token && !isExternal) {
      bearerToken = `Bearer ${token}`;
    } else {
      bearerToken = `Bearer ${External_Bearer_Token}`;
    }

    const headers = { Accept: 'application/json' };
    headers['Authorization'] = bearerToken;
    const res = await fetch(url, {
      method: 'GET',
      headers,
    });
    const text = await res.text();
    try {
      const data = JSON.parse(text);
      if (res.ok && data?.url) {
        const filename = filePath.replace(/^.*[\\/]/, '');
        const link = document.createElement('a');
        link.href = data.url;
        link.download = filename;
        link.click();
      }
    } catch (e) {
      console.error('Non-JSON response:', text);
    }
  }
};

// Extracted function to convert table column data
const convertTableColsData = (
  downloadApiHost: string | undefined,
  tableColsData,
  getClassName
) => {
  return tableColsData.map((row) => {
    const convertedRow = {};
    row.tableData.forEach((col) => {
      const { accessor, value, link, newTab, buttonName } = col;
      const isInternal = row.isInternal;
      let convertedValue = convertTableValue(value);
      convertedRow['internal'] = isInternal ? 'true' : 'false';

      if (col.value.length > 0 && col.link) {
        const data = (
          <Link
            href={link}
            data-testid={'tableLink'}
            newWindow={newTab}
            type={LinkTypes.INLINE}
            iconRight={<LinkIcon />}
          >
            {value}
          </Link>
        );
        convertedRow[accessor] = convertedValue;
        convertedRow['original' + accessor] = data;
        convertedRow[accessor + 'Type'] = typeof convertedRow[
          'original' + accessor
        ];
      } else if (col.link && col.buttonName) {
        convertedRow[accessor] = (
          <div className={getClassName({ descendantName: 'downloadButton' })}>
            <Button
              kind={ButtonKind.BUTTON}
              color={ButtonColor.SECONDARY}
              data-testid="FileDownloadExperience-downloadButton"
              iconPosition={IconPosition?.LEFT}
              icon={<DownloadIcon />}
              onClick={() => downloadFile(downloadApiHost ?? '', link)}
              aria-label={`Download ${link.replace(/^.*[\\/]/, '')}`}
            >
              {buttonName}
            </Button>
          </div>
        );
      } else {
        convertedRow[accessor] = convertedValue;
        convertedRow['original' + accessor] = value;
      }
    });
    return convertedRow;
  });
};

export const FileDownloadExperience: React.FC<IFileDownloadExperience> = (
  props
) => {
  const {
    pageDescription,
    pageTitle,
    searchTitle,
    tableHeaderProps,
    tableDataProps,
    emptySearchMsg,
    baseUrlHost,
    internalUseText,
    fileDownloadUrl,
  } = props;

  const getClassName = getClassNameFactory(FileDownloadExperience.displayName);
  const convertedTableData = convertTableColsData(
    fileDownloadUrl,
    tableDataProps,
    getClassName
  );
  const nonInternalConvertedTableData = convertTableColsData(
    fileDownloadUrl,
    tableDataProps.filter((row) => !row.isInternal),
    getClassName
  );
  const convertedHeaderData = convertToTableHeaderColumns(tableHeaderProps);
  const nonInternalConvertedHeaderData = convertToTableHeaderColumns(
    tableHeaderProps.filter((header) => !header.isInternal)
  );

  const [tableData, setTableData] = useState(nonInternalConvertedTableData);
  const [filterData, setFilterData] = useState(nonInternalConvertedTableData);
  const [tableHeaderData, setTableHeaderData] = useState(
    nonInternalConvertedHeaderData
  );

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [searchFlag, setSearchFlag] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const validateTokenUrl = baseUrlHost + PORTAL_BASE_URL + CIAM_TOKEN_VAL_URL;

  useEffect(() => {
    const token = getValueFromCookie(HS_ACCESS_TOKEN);
    if (token) {
      validateToken(validateTokenUrl, token).then(({ statusCode }) => {
        if (statusCode === StatusCodes.OK_INTERNAL) {
          setIsLoggedIn(true);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      setTableHeaderData(convertedHeaderData);
      setTableData(convertedTableData);
      setFilterData(convertedTableData);
    } else {
      setTableHeaderData(nonInternalConvertedHeaderData);
      setTableData(nonInternalConvertedTableData);
      setFilterData(nonInternalConvertedTableData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const handleSearch = (searchInput) => {
    if (searchInput === '') {
      setFilterData(tableData);
      setSearchFlag(false);
      setSearchTerm('');
      return;
    }

    const searchableColumns = tableHeaderProps.filter(
      (column) => column.searchable
    );

    const filteredData = tableData.filter((item) => {
      for (let column of searchableColumns) {
        let colValue;

        if (typeof item[column.accessor] === 'object') {
          if (column.type === 'link') {
            colValue = (item[column.accessor] as React.ReactElement).props
              .children;
          } else {
            colValue = (item[column.accessor] as React.ReactElement).props
              .children?.props.children;
          }
        } else {
          colValue = item[column.accessor];
        }

        const colValueString = colValue.toString().toLocaleLowerCase();
        const searchInputLower = searchInput.toLocaleLowerCase();

        if (colValueString.includes(searchInputLower)) {
          return true;
        }
      }
      return false;
    });

    setFilterData(filteredData);
    setSearchTerm(searchInput);
    setSearchFlag(true);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchFlag(false);
    setFilterData(tableData);
  };

  return (
    <div
      data-rehydratable={getRehydratableName(
        FileDownloadExperience.displayName
      )}
      {...propsToDataAttributes(props)}
      className={getClassName()}
    >
      <Heading
        className={getClassName({ descendantName: 'pageTitle' })}
        type={types.Medium}
        boldType={boldTypes.medium}
        rank={Rank.First}
      >
        {pageTitle}
      </Heading>
      <VerticalSpacing size={VerticalSpacingSizes.S}> </VerticalSpacing>
      <Typography
        type={Sizes.XXTRASMALL}
        tag={TypographyTags.P}
        className={getClassName({ descendantName: 'pageDescription' })}
      >
        <span dangerouslySetInnerHTML={{ __html: pageDescription }}></span>
      </Typography>
      <VerticalSpacing size={VerticalSpacingSizes.M}> </VerticalSpacing>

      <FileDownloadExperienceSearch
        handleSearch={handleSearch}
        handleClearSearch={clearSearch}
        searchTitle={searchTitle}
      />
      <VerticalSpacing size={VerticalSpacingSizes.S}> </VerticalSpacing>
      <Responsive minWidth={DESKTOP_MIN_WIDTH}>
        {internalUseText && isLoggedIn && (
          <div className={getClassName({ descendantName: 'internalAlert' })}>
            <div
              className={getClassName({ descendantName: 'internalAlertIcon' })}
            >
              {AlertIcon}
            </div>
            <div
              className={getClassName({ descendantName: 'internalAlertText' })}
            >
              {internalUseText}
            </div>
          </div>
        )}
        {filterData.length > 0 ? (
          <FileDownloadExperienceSearchResultDekstop
            data={filterData}
            columns={tableHeaderData}
            serachFlag={searchFlag}
          />
        ) : (
          <EmptySearch
            emptySearchMsg={emptySearchMsg}
            searchTerm={searchTerm}
          />
        )}
      </Responsive>

      <Responsive minWidth={0} maxWidth={MOBILE_MAX_WIDTH}>
        {filterData.length > 0 ? (
          <FileDownloadExperienceSearchResultMobile
            data={filterData}
            columns={tableHeaderData}
            isLoggedIn={isLoggedIn}
            internalUseText={internalUseText}
          />
        ) : (
          <EmptySearch
            emptySearchMsg={emptySearchMsg}
            searchTerm={searchTerm}
          />
        )}
      </Responsive>
    </div>
  );
};

FileDownloadExperience.displayName = 'FileDownloadExperience';
